import React, { useEffect } from "react";
import { useData } from "../../context/DataContext";
import { computeGroupStats } from "../../utils/calculations";
import { fitA4Sheet, resetA4Fit } from "../../utils/printUtils";

export const PrincipalReportCardModal = ({ branchName, onClose }) => {
  const { allStudentsData, loMap, englishResults, mathResults } = useData();
  useEffect(() => {
    const handleBeforePrint = () => {
      const p1 = document.getElementById("principal-a4-fit-1");
      const p2 = document.getElementById("principal-a4-fit-2");
      if (p1) fitA4Sheet(p1);
      if (p2) fitA4Sheet(p2);
    };

    const handleAfterPrint = () => {
      const p1 = document.getElementById("principal-a4-fit-1");
      const p2 = document.getElementById("principal-a4-fit-2");
      if (p1) resetA4Fit(p1);
      if (p2) resetA4Fit(p2);
    };

    window.addEventListener("beforeprint", handleBeforePrint);
    window.addEventListener("afterprint", handleAfterPrint);

    return () => {
      window.removeEventListener("beforeprint", handleBeforePrint);
      window.removeEventListener("afterprint", handleAfterPrint);
    };
  }, []);
  if (!branchName) return null;

  const branchStudents = allStudentsData.filter((s) => s.school === branchName);
  const participated = branchStudents.filter((s) => s.participated);
  const overall = computeGroupStats(branchStudents);
  const grades = [
    ...new Set(branchStudents.map((s) => s.registeredGrade)),
  ].sort((a, b) => a - b);

  const handlePrint = () => {
    window.print();
  };

  const getPct = (v, total) => (total > 0 ? Math.round((v / total) * 100) : 0);

  // Focus Area calculation for Page 2
  const getFocusAreaLOs = (subjectLabel, resultsMap, grade) => {
    const gradeStudents = branchStudents.filter(
      (s) => s.participated && s.registeredGrade === grade,
    );
    const loStats = {};

    gradeStudents.forEach((s) => {
      const data = resultsMap.get(s.id);
      if (!data) return;

      data.answers.forEach((ans, idx) => {
        if (isNaN(ans)) return;
        const loName = loMap.get(`${subjectLabel}-${grade}-${idx + 1}`);
        if (!loName) return;

        if (!loStats[loName]) {
          loStats[loName] = { correct: 0, total: 0 };
        }
        loStats[loName].total++;
        if (ans === 1) loStats[loName].correct++;
      });
    });

    return Object.keys(loStats)
      .map((name) => ({
        name,
        pct: Math.round((loStats[name].correct / loStats[name].total) * 100),
      }))
      .sort((a, b) => a.pct - b.pct)
      .slice(0, 3);
  };

  // Subject readiness calculation for English and Math
  const getSubjectReadiness = (subject) => {
    let sc = 0,
      sb = 0,
      sr = 0;
    participated.forEach((s) => {
      const mastery = subject === "Math" ? s.mathMastery : s.englishMastery;
      const target = Math.min(7, s.registeredGrade);
      const gap = target - mastery;
      if (gap <= 0) sc++;
      else if (gap <= 2) sb++;
      else sr++;
    });
    const total = participated.length;
    return {
      c: sc,
      b: sb,
      r: sr,
      cPct: getPct(sc, total),
      bPct: getPct(sb, total),
      rPct: getPct(sr, total),
    };
  };

  const engReadiness = getSubjectReadiness("English");
  const mathReadiness = getSubjectReadiness("Math");
  return (
    <div
      id="report-modal"
      className="fixed inset-0 z-50 bg-gray-100 flex flex-col h-full w-full select-none"
      style={{ marginTop: "11rem" }}
      aria-labelledby="report-modal-title"
      role="dialog"
      aria-modal="true"
    >
      {/* Top Bar */}
      <div className="no-print flex justify-between items-center gap-3 px-3 sm:px-6 py-3 bg-white border-b border-gray-200 shadow-sm shrink-0">
        <h3
          className="text-base sm:text-lg font-bold text-gray-900 truncate"
          id="report-modal-title"
        >
          School Performance Report — {branchName}
        </h3>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={handlePrint}
            className="px-3 sm:px-4 py-2 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 flex items-center gap-2 text-sm cursor-pointer"
          >
            <span>🖨️</span> <span className="hidden sm:inline">Print</span>
          </button>
          <button
            onClick={onClose}
            className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 font-bold text-sm cursor-pointer"
          >
            ✕ <span className="hidden sm:inline">Close</span>
          </button>
        </div>
      </div>

      {/* Sheets Content */}
      <div
        id="report-modal-scroll"
        className="flex-1 overflow-y-auto px-2 sm:px-4 py-4 sm:py-6 space-y-6"
      >
        {/* ===== PAGE 1 ===== */}
        <div id="principal-a4-sheet-1" className="report-a4-sheet">
          <div id="principal-a4-fit-1" className="report-a4-fit">
            {/* Header Box */}
            <div
              className="bg-white p-3 sm:p-4 rounded-2xl shadow-lg mb-3 print-avoid-break"
              style={{ border: "2px solid #347363" }}
            >
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_1fr] items-center gap-3 sm:gap-4 print:!grid-cols-[1fr_2fr_1fr] print:!gap-4">
                <div className="flex items-center justify-center sm:justify-start space-x-3">
                  <img
                    src="https://www.northsouth.org/wp-content/uploads/2024/09/nsf-logov1.png"
                    alt="School Logo"
                    className="h-14 w-auto rounded-lg shadow-md bg-white p-1"
                    onError={(e) => {
                      e.target.src = "https://placehold.co/100x100?text=LOGO";
                    }}
                  />
                </div>
                <div className="text-center">
                  <div
                    className="font-bold uppercase"
                    style={{
                      fontFamily: "Georgia, 'Times New Roman', serif",
                      fontSize: "21px",
                      letterSpacing: "0.5px",
                      color: "#357a64",
                    }}
                  >
                    School Performance Report
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    Samagra Assessment • Principal Summary
                  </div>
                </div>
                <div></div>
              </div>

              <div className="h-px bg-gray-100 my-2.5"></div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 print:!grid-cols-3 print:!gap-6">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-1">
                    School
                  </p>
                  <p
                    className="text-base font-bold"
                    style={{ color: "#2450D3" }}
                  >
                    {branchName}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-1">
                    Assessment
                  </p>
                  <p className="text-base font-bold text-gray-900">
                    SAMAGRA-1 Assessment 2026-27
                  </p>
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-1">
                    Grades Assessed
                  </p>
                  <p className="text-base font-bold text-gray-900">
                    {grades.map((g) => `Grade ${g}`).join(", ")}
                  </p>
                </div>
              </div>
            </div>

            {/* Registration & Participation Table */}
            <div className="flex items-center justify-between flex-wrap gap-3 mb-1.5">
              <h3 className="text-base font-bold text-gray-900">
                Registration &amp; Participation
              </h3>
              <div className="flex flex-wrap gap-4 text-xs font-semibold">
                <span className="flex items-center gap-1">
                  <span
                    className="w-2.5 h-2.5 rounded-sm inline-block"
                    style={{ background: "#16a34a" }}
                  ></span>
                  <span className="text-gray-700">
                    <b style={{ color: "#166534" }}>Good</b> above 75%
                  </span>
                </span>
                <span className="flex items-center gap-1">
                  <span
                    className="w-2.5 h-2.5 rounded-sm inline-block"
                    style={{ background: "#d97706" }}
                  ></span>
                  <span className="text-gray-700">
                    <b style={{ color: "#b45309" }}>Moderate</b> 50-75%
                  </span>
                </span>
                <span className="flex items-center gap-1">
                  <span
                    className="w-2.5 h-2.5 rounded-sm inline-block"
                    style={{ background: "#dc2626" }}
                  ></span>
                  <span className="text-gray-700">
                    <b style={{ color: "#b91c1c" }}>Low</b> below 50%
                  </span>
                </span>
              </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-200 mb-3 print-avoid-break">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-100 text-gray-700 font-bold border-b border-gray-200">
                    <th className="py-2 px-3 text-left">Metric</th>
                    {grades.map((g) => (
                      <th key={g} className="py-2 px-3 text-center">
                        Grade {g}
                      </th>
                    ))}
                    <th className="py-2 px-3 text-center bg-gray-200">
                      Total / Overall
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="py-2 px-3 font-semibold text-gray-800">
                      Enrolled
                    </td>
                    {grades.map((g) => {
                      const count = branchStudents.filter(
                        (s) => s.registeredGrade === g,
                      ).length;
                      return (
                        <td key={g} className="py-2 px-3 text-center">
                          {count}
                        </td>
                      );
                    })}
                    <td className="py-2 px-3 text-center font-bold">
                      {branchStudents.length}
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-2 px-3 font-semibold text-gray-800">
                      Assessed
                    </td>
                    {grades.map((g) => {
                      const count = branchStudents.filter(
                        (s) => s.registeredGrade === g && s.participated,
                      ).length;
                      return (
                        <td
                          key={g}
                          className="py-2 px-3 text-center text-green-700 font-bold"
                        >
                          {count}
                        </td>
                      );
                    })}
                    <td className="py-2 px-3 text-center font-bold text-green-700">
                      {participated.length}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 font-semibold text-gray-800">
                      Participation %
                    </td>
                    {grades.map((g) => {
                      const gAll = branchStudents.filter(
                        (s) => s.registeredGrade === g,
                      );
                      const gPart = gAll.filter((s) => s.participated).length;
                      const rate = getPct(gPart, gAll.length);
                      return (
                        <td
                          key={g}
                          className="py-2 px-3 text-center font-black"
                          style={{
                            color:
                              rate >= 75
                                ? "#166534"
                                : rate >= 50
                                  ? "#b45309"
                                  : "#b91c1c",
                          }}
                        >
                          {rate}%
                        </td>
                      );
                    })}
                    <td
                      className="py-2 px-3 text-center font-black bg-gray-50"
                      style={{
                        color:
                          getPct(participated.length, branchStudents.length) >=
                          75
                            ? "#166534"
                            : "#b45309",
                      }}
                    >
                      {getPct(participated.length, branchStudents.length)}%
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* School Snapshot: 4 KPI cards */}
            <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
              <h3 className="text-base font-bold text-gray-900">
                School Snapshot
              </h3>
              <div className="flex flex-wrap gap-4 text-xs font-semibold">
                <span className="flex items-center gap-1">
                  <span
                    className="w-2.5 h-2.5 rounded-sm inline-block"
                    style={{ background: "#16a34a" }}
                  ></span>
                  <span className="text-gray-700">
                    <b style={{ color: "#166534" }}>Competent</b> passed all
                    levels
                  </span>
                </span>
                <span className="flex items-center gap-1">
                  <span
                    className="w-2.5 h-2.5 rounded-sm inline-block"
                    style={{ background: "#f59e0b" }}
                  ></span>
                  <span className="text-gray-700">
                    <b style={{ color: "#b45309" }}>Bridge the Gap</b> 1-2 yrs
                    below
                  </span>
                </span>
                <span className="flex items-center gap-1">
                  <span
                    className="w-2.5 h-2.5 rounded-sm inline-block"
                    style={{ background: "#dc2626" }}
                  ></span>
                  <span className="text-gray-700">
                    <b style={{ color: "#b91c1c" }}>Remediation Need</b> 3+ yrs
                    below
                  </span>
                </span>
              </div>
            </div>
            <p className="text-[10px] italic text-gray-400 mb-1.5">
              *A single weak level (60-79%) is forgiven if every other level is
              ≥80%.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3 print:!grid-cols-4">
              <div
                className="bg-white rounded-xl p-3 shadow border-l-4"
                style={{ borderColor: "#1e3a8a" }}
              >
                <p className="text-[10px] text-gray-400 font-bold uppercase">
                  Students Assessed
                </p>
                <p
                  className="text-2xl font-black mt-0.5"
                  style={{ color: "#1e3a8a" }}
                >
                  {overall.participated}
                </p>
                <p className="text-[10px] text-gray-400">
                  of {overall.total} enrolled
                </p>
              </div>
              <div
                className="bg-white rounded-xl p-3 shadow border-l-4"
                style={{ borderColor: "#166534" }}
              >
                <p className="text-[10px] text-gray-400 font-bold uppercase">
                  At Grade Level
                </p>
                <p
                  className="text-2xl font-black mt-0.5"
                  style={{ color: "#166534" }}
                >
                  {getPct(overall.comp, overall.participated)}%
                </p>
                <p className="text-[10px] text-gray-400">
                  {overall.comp} students
                </p>
              </div>
              <div
                className="bg-white rounded-xl p-3 shadow border-l-4"
                style={{ borderColor: "#b45309" }}
              >
                <p className="text-[10px] text-gray-400 font-bold uppercase">
                  Bridge the Gap
                </p>
                <p
                  className="text-2xl font-black mt-0.5"
                  style={{ color: "#b45309" }}
                >
                  {getPct(overall.bridge, overall.participated)}%
                </p>
                <p className="text-[10px] text-gray-400">
                  {overall.bridge} students
                </p>
              </div>
              <div
                className="bg-white rounded-xl p-3 shadow border-l-4"
                style={{ borderColor: "#b91c1c" }}
              >
                <p className="text-[10px] text-gray-400 font-bold uppercase">
                  Remediation Need
                </p>
                <p
                  className="text-2xl font-black mt-0.5"
                  style={{ color: "#b91c1c" }}
                >
                  {getPct(overall.remed, overall.participated)}%
                </p>
                <p className="text-[10px] text-gray-400">
                  {overall.remed} students
                </p>
              </div>
            </div>

            {/* Subject Readiness Distribution */}
            <h3 className="text-base font-bold text-gray-900 mb-1.5">
              Subject Readiness Distribution
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3 print:!grid-cols-2">
              <div
                className="bg-white rounded-xl p-3 shadow border-l-4"
                style={{ borderColor: "#16a34a" }}
              >
                <div className="flex justify-between items-center text-xs font-bold text-gray-800 mb-1">
                  <span>📗 English</span>
                  <span className="text-gray-400">
                    {participated.length} students
                  </span>
                </div>
                <div className="flex rounded-full overflow-hidden h-3 mb-2 bg-gray-100">
                  <div
                    style={{
                      background: "#166534",
                      width: `${engReadiness.cPct}%`,
                    }}
                  ></div>
                  <div
                    style={{
                      background: "#b45309",
                      width: `${engReadiness.bPct}%`,
                    }}
                  ></div>
                  <div
                    style={{
                      background: "#b91c1c",
                      width: `${engReadiness.rPct}%`,
                    }}
                  ></div>
                </div>
                <div className="flex justify-between text-[10px] text-gray-600 font-bold">
                  <span style={{ color: "#166534" }}>
                    ● At: {engReadiness.cPct}%
                  </span>
                  <span style={{ color: "#b45309" }}>
                    ● Bridge: {engReadiness.bPct}%
                  </span>
                  <span style={{ color: "#b91c1c" }}>
                    ● Remed: {engReadiness.rPct}%
                  </span>
                </div>
              </div>

              <div
                className="bg-white rounded-xl p-3 shadow border-l-4"
                style={{ borderColor: "#2450D3" }}
              >
                <div className="flex justify-between items-center text-xs font-bold text-gray-800 mb-1">
                  <span>📐 Mathematics</span>
                  <span className="text-gray-400">
                    {participated.length} students
                  </span>
                </div>
                <div className="flex rounded-full overflow-hidden h-3 mb-2 bg-gray-100">
                  <div
                    style={{
                      background: "#166534",
                      width: `${mathReadiness.cPct}%`,
                    }}
                  ></div>
                  <div
                    style={{
                      background: "#b45309",
                      width: `${mathReadiness.bPct}%`,
                    }}
                  ></div>
                  <div
                    style={{
                      background: "#b91c1c",
                      width: `${mathReadiness.rPct}%`,
                    }}
                  ></div>
                </div>
                <div className="flex justify-between text-[10px] text-gray-600 font-bold">
                  <span style={{ color: "#166534" }}>
                    ● At: {mathReadiness.cPct}%
                  </span>
                  <span style={{ color: "#b45309" }}>
                    ● Bridge: {mathReadiness.bPct}%
                  </span>
                  <span style={{ color: "#b91c1c" }}>
                    ● Remed: {mathReadiness.rPct}%
                  </span>
                </div>
              </div>
            </div>

            {/* Grade-wise Achievement Level */}
            <h3 className="text-base font-bold text-gray-900 mb-1.5">
              Grade-wise Achievement Level
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 print:!grid-cols-2">
              {/* English Matrix */}
              {/* <div className="border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-[11px] border-collapse text-center">
                  <thead>
                    <tr className="bg-green-700 text-white font-bold">
                      <th className="p-1.5 text-left pl-2">English</th>
                      <th className="p-1.5">At Grade</th>
                      <th className="p-1.5">1-2 Below</th>
                      <th className="p-1.5">3-4 Below</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grades.map(g => {
                      const gStudents = branchStudents.filter(s => s.registeredGrade === g && s.participated);
                      let c = 0, b = 0, r = 0;
                      gStudents.forEach(s => {
                        const gap = Math.min(7, g) - s.englishMastery;
                        if (gap <= 0) c++;
                        else if (gap <= 2) b++;
                        else r++;
                      });
                      return (
                        <tr key={g} className="border-b border-gray-100">
                          <td className="p-1.5 text-left pl-2 font-bold text-gray-700">Grade {g}</td>
                          <td className="p-1.5 font-bold" style={{ color: '#166534' }}>{c || '—'}</td>
                          <td className="p-1.5 font-bold" style={{ color: '#b45309' }}>{b || '—'}</td>
                          <td className="p-1.5 font-bold" style={{ color: '#b91c1c' }}>{r || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div> */}
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-[11px] border-collapse text-center">
                  <thead>
                    <tr className="bg-green-700 text-white font-bold">
                      <th className="p-1.5 text-left pl-2">English</th>
                      <th className="p-1.5">At Grade</th>
                      <th className="p-1.5">1 Year Below</th>
                      <th className="p-1.5">2 Years Below</th>
                      <th className="p-1.5">3 Years Below</th>
                      <th className="p-1.5">4 Years Below</th>
                    </tr>
                  </thead>

                  <tbody>
                    {grades.map((g) => {
                      const gStudents = branchStudents.filter(
                        (s) => s.registeredGrade === g && s.participated,
                      );

                      let atGrade = 0;
                      let oneBelow = 0;
                      let twoBelow = 0;
                      let threeBelow = 0;
                      let fourBelow = 0;

                      gStudents.forEach((s) => {
                        const gap = Math.min(7, g) - s.englishMastery;
                        if (gap <= 0) {
                          atGrade++;
                        } else if (gap === 1) {
                          oneBelow++;
                        } else if (gap === 2) {
                          twoBelow++;
                        } else if (gap === 3) {
                          threeBelow++;
                        } else if (gap === 4) {
                          fourBelow++;
                        }
                      });

                      return (
                        <tr key={g} className="border-b border-gray-100">
                          <td className="p-1.5 text-left pl-2 font-bold text-gray-700">
                            {" "}
                            Grade {g}{" "}
                          </td>
                          <td
                            className="p-1.5 font-bold"
                            style={{ color: "#166534" }}
                          >
                            {" "}
                            {atGrade || "—"}
                          </td>
                          <td
                            className="p-1.5 font-bold"
                            style={{ color: "#b45309" }}
                          >
                            {" "}
                            {oneBelow || "—"}{" "}
                          </td>
                          <td
                            className="p-1.5 font-bold"
                            style={{ color: "#b45309" }}
                          >
                            {" "}
                            {twoBelow || "—"}{" "}
                          </td>
                          <td
                            className="p-1.5 font-bold"
                            style={{ color: "#b91c1c" }}
                          >
                            {" "}
                            {threeBelow || "—"}{" "}
                          </td>
                          <td
                            className="p-1.5 font-bold"
                            style={{ color: "#b91c1c" }}
                          >
                            {" "}
                            {fourBelow || "—"}{" "}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* Math Matrix */}
              {/* <div className="border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-[11px] border-collapse text-center">
                  <thead>
                    <tr className="bg-blue-700 text-white font-bold">
                      <th className="p-1.5 text-left pl-2">Mathematics</th>
                      <th className="p-1.5">At Grade</th>
                      <th className="p-1.5">1-2 Below</th>
                      <th className="p-1.5">3-4 Below</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grades.map(g => {
                      const gStudents = branchStudents.filter(s => s.registeredGrade === g && s.participated);
                      let c = 0, b = 0, r = 0;
                      gStudents.forEach(s => {
                        const gap = Math.min(7, g) - s.mathMastery;
                        if (gap <= 0) c++;
                        else if (gap <= 2) b++;
                        else r++;
                      });
                      return (
                        <tr key={g} className="border-b border-gray-100">
                          <td className="p-1.5 text-left pl-2 font-bold text-gray-700">Grade {g}</td>
                          <td className="p-1.5 font-bold" style={{ color: '#166534' }}>{c || '—'}</td>
                          <td className="p-1.5 font-bold" style={{ color: '#b45309' }}>{b || '—'}</td>
                          <td className="p-1.5 font-bold" style={{ color: '#b91c1c' }}>{r || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div> */}
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-[11px] border-collapse text-center">
                  <thead>
                    <tr className="bg-blue-700 text-white font-bold">
                      <th className="p-1.5 text-left pl-2">Mathematics</th>
                      <th className="p-1.5">At Grade</th>
                      <th className="p-1.5">1 Year Below</th>
                      <th className="p-1.5">2 Years Below</th>
                      <th className="p-1.5">3 Years Below</th>
                      <th className="p-1.5">4 Years Below</th>
                    </tr>
                  </thead>

                  <tbody>
                    {grades.map((g) => {
                      const gStudents = branchStudents.filter(
                        (s) => s.registeredGrade === g && s.participated,
                      );

                      let atGrade = 0;
                      let oneBelow = 0;
                      let twoBelow = 0;
                      let threeBelow = 0;
                      let fourBelow = 0;

                      gStudents.forEach((s) => {
                        const gap = Math.min(7, g) - s.mathMastery;

                        if (gap <= 0) {
                          atGrade++;
                        } else if (gap === 1) {
                          oneBelow++;
                        } else if (gap === 2) {
                          twoBelow++;
                        } else if (gap === 3) {
                          threeBelow++;
                        } else if (gap === 4) {
                          fourBelow++;
                        }
                      });

                      return (
                        <tr key={g} className="border-b border-gray-100">
                          <td className="p-1.5 text-left pl-2 font-bold text-gray-700">
                            {" "}
                            Grade {g}{" "}
                          </td>
                          <td
                            className="p-1.5 font-bold"
                            style={{ color: "#166534" }}
                          >
                            {" "}
                            {atGrade || "—"}{" "}
                          </td>
                          <td
                            className="p-1.5 font-bold"
                            style={{ color: "#b45309" }}
                          >
                            {" "}
                            {oneBelow || "—"}{" "}
                          </td>
                          <td
                            className="p-1.5 font-bold"
                            style={{ color: "#b45309" }}
                          >
                            {" "}
                            {twoBelow || "—"}{" "}
                          </td>
                          <td
                            className="p-1.5 font-bold"
                            style={{ color: "#b91c1c" }}
                          >
                            {" "}
                            {threeBelow || "—"}{" "}
                          </td>
                          <td
                            className="p-1.5 font-bold"
                            style={{ color: "#b91c1c" }}
                          >
                            {" "}
                            {fourBelow || "—"}{" "}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="text-[10px] text-gray-400 mt-1">
              Student counts by achievement level —{" "}
              <b style={{ color: "#166534" }}>green</b> = at grade level,{" "}
              <b style={{ color: "#b45309" }}>amber</b> = 1-2 years below,{" "}
              <b style={{ color: "#b91c1c" }}>red</b> = 3-4 years below. Dash
              (—) = no students.
            </p>
          </div>
        </div>

        {/* ===== PAGE 2 ===== */}
        <div id="principal-a4-sheet-2" className="report-a4-sheet">
          <div id="principal-a4-fit-2" className="report-a4-fit">
            {/* Header Box */}
            <div
              className="flex items-center justify-between flex-wrap gap-3 rounded-2xl p-3 sm:p-4 print-avoid-break mb-3"
              style={{ border: "2px solid #347363" }}
            >
              <div className="flex items-center space-x-3">
                <img
                  src="https://www.northsouth.org/wp-content/uploads/2024/09/nsf-logov1.png"
                  alt="School Logo"
                  className="h-10 w-auto rounded-lg shadow-md bg-white p-1"
                  onError={(e) => {
                    e.target.src = "https://placehold.co/100x100?text=LOGO";
                  }}
                />
              </div>
              <div className="text-right">
                <div
                  className="font-bold uppercase"
                  style={{
                    // fontFamily: "Georgia, 'Times New Roman', serif",
                    fontSize: "20px",
                    letterSpacing: "0.5px",
                    color: "#357a64",
                  }}
                >
                  {" "}
                  Grade-wise Focus Areas
                </div>
                <div className="text-xs text-gray-400 mt-0.5">
                  {branchName} • SAMAGRA-1 2026-27
                </div>
              </div>
            </div>

            <p className="text-xs text-gray-500 my-3 leading-relaxed">
              Bottom three learning objectives (LOs) per grade, with each LO's
              average score — <b style={{ color: "#16a34a" }}>green</b> above
              75%, <b style={{ color: "#d97706" }}>amber</b> 50-75%,{" "}
              <b style={{ color: "#dc2626" }}>red</b> below 50%.
            </p>

            {/* English Focus Area */}
            <div className="flex items-center gap-2 mb-2">
              <span
                className="w-1.5 h-4 rounded"
                style={{ background: "#16a34a" }}
              ></span>
              <span className="text-base font-black text-gray-900">
                English Focus Area
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5 print:!grid-cols-2">
              {grades.map((g) => {
                const los = getFocusAreaLOs("English", englishResults, g);
                return (
                  <div
                    key={g}
                    className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm"
                  >
                    <h4 className="font-bold text-xs text-gray-800 mb-2 border-b pb-1">
                      Grade {g}
                    </h4>
                    {los.length === 0 ? (
                      <p className="text-[10px] text-gray-400 italic">
                        No LO data
                      </p>
                    ) : (
                      <div className="space-y-1.5">
                        {los.map((item, idx) => (
                          <div
                            key={idx}
                            className="flex justify-between items-center text-xs"
                          >
                            <span
                              className="text-gray-700 truncate pr-2 text-[11px]"
                              title={item.name}
                            >
                              {idx + 1}. {item.name}
                            </span>
                            <span
                              className="font-bold shrink-0"
                              style={{
                                color:
                                  item.pct >= 75
                                    ? "#16a34a"
                                    : item.pct >= 50
                                      ? "#d97706"
                                      : "#dc2626",
                              }}
                            >
                              {" "}
                              {item.pct}%
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Math Focus Area */}
            <div className="flex items-center gap-2 mb-2">
              <span
                className="w-1.5 h-4 rounded"
                style={{ background: "#2450D3" }}
              ></span>
              <span className="text-base font-black text-gray-900">
                Math Focus Area
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5 print:!grid-cols-2">
              {grades.map((g) => {
                const los = getFocusAreaLOs("Math", mathResults, g);
                return (
                  <div
                    key={g}
                    className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm"
                  >
                    <h4 className="font-bold text-xs text-gray-800 mb-2 border-b pb-1">
                      Grade {g}
                    </h4>
                    {los.length === 0 ? (
                      <p className="text-[10px] text-gray-400 italic">
                        No LO data
                      </p>
                    ) : (
                      <div className="space-y-1.5">
                        {los.map((item, idx) => (
                          <div
                            key={idx}
                            className="flex justify-between items-center text-xs"
                          >
                            <span
                              className="text-gray-700 truncate pr-2 text-[11px]"
                              title={item.name}
                            >
                              {idx + 1}. {item.name}
                            </span>
                            <span
                              className="font-bold shrink-0"
                              style={{
                                color:
                                  item.pct >= 75
                                    ? "#16a34a"
                                    : item.pct >= 50
                                      ? "#d97706"
                                      : "#dc2626",
                              }}
                            >
                              {" "}
                              {item.pct}%
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Print Footer */}
            <div className="mt-4 pt-3 border-t border-dashed border-gray-200 text-xs text-gray-400 leading-relaxed">
              <p className="font-semibold text-gray-500">
                Generated by NorthSouth Foundation • SAMAGRA-1 Assessment
              </p>
              <p>Confidential — for school leadership use only</p>
              <p>
                Printed on:{" "}
                <span className="font-semibold text-gray-500">
                  {new Date().toLocaleDateString()}
                </span>
              </p>
              <a
                href="https://www.northsouth.org"
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold"
                style={{ color: "#347363" }}
              >
                www.northsouth.org
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default PrincipalReportCardModal;
